import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import CheatSheetContent from "../components/CheatSheetContent";
import SearchBar from "../components/SearchBar";
import {
  FaCode,
  FaDatabase,
  FaChartBar,
  FaGitAlt,
  FaDocker,
  FaSearch,
  FaCoffee,
  FaEnvelope,
  FaChevronDown,
  FaBars,
  FaTimes,
  FaPalette,
  FaSun,
  FaMoon,
} from "react-icons/fa";
import {
  DiJavascript,
  DiPython,
  DiReact,
  DiNodejs,
  DiGit,
  DiMongodb,
  DiCss3,
  DiHtml5,
} from "react-icons/di";
import {
  SiTypescript,
  SiExpress,
  SiTableau,
  SiPowerbi,
  SiMysql,
  SiMicrosoftexcel,
  SiFigma,
} from "react-icons/si";
import "../styles/CheatSheetPage.css";
import "../styles/Home.css";
import { useTheme } from "../context/ThemeContext";

// Import cheatsheets
import githubCheatSheet from "../data/githubCheatSheet";
import dockerCheatsheet from "../data/dockerCheatsheet";
import reactCheatsheet from "../data/reactCheatsheet";
import typescriptCheatsheet from "../data/typescriptCheatsheet";
import nodejsCheatsheet from "../data/nodejsCheatsheet";
import mongodbCheatsheet from "../data/mongodbCheatsheet";
import expressCheatsheet from "../data/expressCheatsheet";
import excelCheatSheet from "../data/excelCheatSheet";
import tableauPowerBICheatsheet from "../data/tableauPowerBICheatsheet";
import sqlCheatSheet from "../data/sqlCheatSheet";
import javascriptCheatSheet from "../data/javascriptCheatSheet";
import pythonCheatsheet from "../data/pythonCheatsheet";
import html5CheatSheet from "../data/html5CheatSheet";
import cssCheatSheet from "../data/cssCheatSheet";
import phpCheatSheet from "../data/phpCheatSheet";
import tableauCheatSheet from "../data/tableauCheatSheet";
import powerBICheatSheet from "../data/powerBICheatSheet";
import gitCheatsheet from "../data/gitCheatsheet";
import uxuiCheatSheet from "../data/uxuiCheatSheet";

const cheatSheets = {
  github: githubCheatSheet,
  docker: dockerCheatsheet,
  react: reactCheatsheet,
  typescript: typescriptCheatsheet,
  nodejs: nodejsCheatsheet,
  mongodb: mongodbCheatsheet,
  express: expressCheatsheet,
  excel: excelCheatSheet,
  "tableau-powerbi": tableauPowerBICheatsheet,
  sql: sqlCheatSheet,
  javascript: javascriptCheatSheet,
  python: pythonCheatsheet,
  html: html5CheatSheet,
  css: cssCheatSheet,
  php: phpCheatSheet,
  tableau: tableauCheatSheet,
  powerbi: powerBICheatSheet,
  html5: html5CheatSheet,
  git: gitCheatsheet,
  "ux-ui": uxuiCheatSheet,
};

const CheatSheetPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [cheatSheet, setCheatSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const allNavItems = [
    { path: "/", label: "Home" },
    { path: "/cheatsheet/html", label: "HTML" },
    { path: "/cheatsheet/css", label: "CSS" },
    { path: "/cheatsheet/javascript", label: "JavaScript" },
    { path: "/cheatsheet/react", label: "React" },
    { path: "/cheatsheet/python", label: "Python" },
    { path: "/cheatsheet/php", label: "PHP" },
    { path: "/cheatsheet/typescript", label: "TypeScript" },
    { path: "/cheatsheet/ux-ui", label: "UX/UI Design" },
    { path: "/cheatsheet/nodejs", label: "Node.js" },
    { path: "/cheatsheet/express", label: "Express" },
    { path: "/cheatsheet/mongodb", label: "MongoDB" },
    { path: "/cheatsheet/sql", label: "SQL" },
    { path: "/cheatsheet/git", label: "Git" },
    { path: "/cheatsheet/docker", label: "Docker" },
    { path: "/cheatsheet/excel", label: "Excel" },
    { path: "/cheatsheet/tableau", label: "Tableau" },
    { path: "/cheatsheet/powerbi", label: "Power BI" },
  ];

  const mainNavItems = allNavItems.slice(0, 7);
  const moreNavItems = allNavItems.slice(7);

  const loadCheatSheet = useCallback(() => {
    setLoading(true);
    setError(null);

    try {
      const data = cheatSheets[id];
      if (data) {
        setCheatSheet(data);
      } else {
        setError("Cheatsheet not found");
      }
    } catch (err) {
      setError("Error loading cheatsheet");
      console.error("Error loading cheatsheet:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCheatSheet();
  }, [loadCheatSheet]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading cheatsheet...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate("/")}>Go Back</button>
      </div>
    );
  }

  if (!cheatSheet) {
    return (
      <div className="error-container">
        <h2>Cheatsheet Not Found</h2>
        <p>The requested cheatsheet could not be found.</p>
        <button onClick={() => navigate("/")}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="cheatsheet-page">
      <header className="top-nav">
        <div className="nav-brand">
          <Link to="/">Himal Block CheatSheets</Link>
        </div>
        <SearchBar />
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          {isDark ? <FaSun /> : <FaMoon />}
        </button>
        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
        <div className={`nav-links ${isMobileMenuOpen ? "active" : ""}`}>
          {mainNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <div className="more-dropdown">
            <button onClick={() => setShowMore(!showMore)}>
              More <FaChevronDown className={showMore ? "rotate" : ""} />
            </button>
            <div className={`dropdown-content ${showMore ? "show" : ""}`}>
              {moreNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setShowMore(false);
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="cheatsheet-content">
        <CheatSheetContent content={cheatSheet} />
      </main>
    </div>
  );
};

export default CheatSheetPage;
